/**
 * OrganizatorProjeDetayPage.jsx — organizatörün SALT OKUNUR proje ayrıntısı (backlog #451, 2026-10-17; tasarım #445).
 *
 * Organizatör, PROJELER alanında İzleme ya da Denetim kademesindeyse bir projenin ayrıntılarını ve dosya LİSTESİNİ görür:
 * stand, firmalar, stand tipi, durum, gerekli belgeler ve onay durumları, yüklenen dosyaların adları ve durumları, revizyonlar.
 * Dosyaların kendisi yoktur: backend organizatöre dosyanın saklı adını vermez (proje dosyaları adıyla sunulur), bu sayfa da açma / indirme
 * bağlantısı çizmez. Hiçbir şey yazılmaz: değiştirmek için projenin Dekoratörü (WRITE) gerekir.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Alert, Badge, Card, CardBody, CardHeader, Col, Container, Row, Spinner, Table } from "reactstrap";
import { ArrowLeft, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { getOrganizatorProjeDetay } from "../../services/portalService";

const ONAY_RENK = { 0: "warning", 1: "success", 2: "danger" };
const REVIZYON_RENK = { OPEN: "warning", ANSWERED: "info", RESOLVED: "success" };

/** A project's required documents with the files uploaded against each: [{standFileId, ad, onay, dosyalar: [...]}]. Pure, for the page and its test. */
export function belgeSatirlari(detay) {
  const yuklenen = detay?.uploadedFiles ?? [];
  const onaylar = new Map((detay?.fileApprovals ?? []).map((a) => [Number(a.standFileId), a.status]));
  const gerekli = detay?.requiredFiles ?? [];
  const satirlar = gerekli.map((rf) => {
    const id = Number(rf.stand_file_id);
    return {
      standFileId: id,
      ad: rf.stand_file_shortname || rf.stand_file_name || `#${id}`,
      onay: onaylar.get(id) ?? 0,
      dosyalar: yuklenen.filter((f) => Number(f.standFileId) === id),
    };
  });
  // files uploaded against a document that is no longer in the required list are still listed, so nothing the company sent is hidden
  const bilinen = new Set(satirlar.map((s) => s.standFileId));
  const artik = yuklenen.filter((f) => !bilinen.has(Number(f.standFileId)));
  if (artik.length > 0) {
    satirlar.push({ standFileId: null, ad: null, onay: null, dosyalar: artik });
  }
  return satirlar;
}

export default function OrganizatorProjeDetayPage() {
  const { t } = useTranslation("portal");
  const { projeId } = useParams();
  const [detay, setDetay] = useState(null);
  const [hata, setHata] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);

  useEffect(() => { document.title = i18n.t("portal:organizator.organizatorPaneliFds"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(null);
      try {
        const out = await getOrganizatorProjeDetay(projeId);
        if (!ignore) setDetay(out);
      } catch (e) {
        if (!ignore) setHata(e?.message || i18n.t("portal:organizator.projeDetay.yuklenemedi"));
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    if (projeId) run();
    return () => { ignore = true; };
  }, [projeId]);

  const satirlar = useMemo(() => belgeSatirlari(detay), [detay]);
  const revizyonlar = detay?.revisions ?? [];

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-2">
          <Link to="/Firma/Organizator/Projeler" className="small" data-testid="proje-geri">
            <ArrowLeft size={14} className="me-1" />{t("organizator.projeDetay.geri")}
          </Link>
        </div>
        {hata && <Alert color="danger" className="small">{hata}</Alert>}
        {yukleniyor && !detay && <Spinner size="sm" color="primary" />}
        {detay && (
          <>
            <Alert color="light" className="small border py-2" data-testid="proje-salt-okunur">
              <Lock size={13} className="me-1" />{t("organizator.projeDetay.saltOkunur")}
            </Alert>
            <Card className="mb-3">
              <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
                <h5 className="card-title mb-0 flex-grow-1">{t("organizator.projeDetay.baslik")}{" · "}{detay.standName}</h5>
                {detay.serinProje && <Badge color="info">{t("organizator.projeDetay.serin")}</Badge>}
                {revizyonlar.some((r) => r.status !== "RESOLVED") && <Badge color="warning">{t("organizator.revizyonda")}</Badge>}
                <Badge color="light" className="text-muted border">{t("organizator.insaIzni")}{" "}{detay.statusId ?? "—"}</Badge>
              </CardHeader>
              <CardBody>
                <Row className="g-3 small">
                  <Col md={4}><div className="text-muted">{t("organizator.stand")}</div><div className="fw-semibold">{detay.standName || "—"}</div></Col>
                  <Col md={4}><div className="text-muted">{t("organizator.salon")}</div><div className="fw-semibold">{detay.salonName || "—"}</div></Col>
                  <Col md={4}><div className="text-muted">{t("organizator.standTipi")}</div><div className="fw-semibold">{detay.standTypeName || "—"}</div></Col>
                  <Col md={4}><div className="text-muted">{t("organizator.katilimci")}</div><div className="fw-semibold">{detay.firmaName || "—"}</div></Col>
                  <Col md={4}><div className="text-muted">{t("organizator.dekorator")}</div><div className="fw-semibold">{detay.dekoratorFirmaName || "—"}</div></Col>
                  <Col md={4}>
                    <div className="text-muted">{t("organizator.projeDetay.yukseklik")}</div>
                    <div className="fw-semibold">{detay.standHeightName || (detay.projectHeightNumber != null ? `${detay.projectHeightNumber} m` : "—")}</div>
                  </Col>
                  <Col md={12}>
                    <Badge color={detay.approvedCount >= detay.requiredCount && detay.requiredCount > 0 ? "success" : "secondary"} data-testid="proje-ilerleme">
                      {t("organizator.projeDetay.ilerleme", { onaylanan: detay.approvedCount ?? 0, gerekli: detay.requiredCount ?? 0 })}
                    </Badge>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="mb-3">
              <CardHeader><strong>{t("organizator.projeDetay.belgeler")}</strong></CardHeader>
              <CardBody>
                <div className="table-responsive">
                  <Table size="sm" className="align-middle mb-0">
                    <thead className="table-light">
                      <tr><th>{t("organizator.projeDetay.belge")}</th><th>{t("organizator.durum")}</th><th>{t("organizator.projeDetay.dosyalar")}</th></tr>
                    </thead>
                    <tbody>
                      {satirlar.length === 0 && <tr><td colSpan={3} className="text-center text-muted py-3">{t("organizator.projeDetay.belgeYok")}</td></tr>}
                      {satirlar.map((s) => (
                        <tr key={s.standFileId ?? "diger"}>
                          <td className="fw-semibold small">{s.ad ?? t("organizator.projeDetay.digerDosyalar")}</td>
                          <td>{s.onay != null && <Badge color={ONAY_RENK[s.onay] ?? "secondary"}>{t(`organizator.projeDetay.onay.${s.onay}`)}</Badge>}</td>
                          <td className="small">
                            {s.dosyalar.length === 0 && <span className="text-muted">{t("organizator.projeDetay.dosyaYok")}</span>}
                            {s.dosyalar.map((f) => (
                              <div key={f.id ?? `${f.standFileId}-${f.originalFileName}`}>
                                {/* a name and a status, no link: the organizer is not offered the file (#451) */}
                                <span data-testid="proje-dosya-adi">{f.originalFileName || "—"}</span>{" "}
                                <Badge color={ONAY_RENK[f.approvalStatus ?? 0] ?? "secondary"} pill>{t(`organizator.projeDetay.onay.${f.approvalStatus ?? 0}`)}</Badge>
                                {f.rejectReason && <div className="text-danger">{t("organizator.projeDetay.retNedeni")}{": "}{f.rejectReason}</div>}
                              </div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>

            <Card className="mb-3">
              <CardHeader><strong>{t("organizator.projeDetay.revizyonlar")}</strong></CardHeader>
              <CardBody>
                {revizyonlar.length === 0 && <span className="small text-muted">{t("organizator.projeDetay.revizyonYok")}</span>}
                {revizyonlar.map((r) => (
                  <div key={r.id} className="small border-bottom py-2">
                    <Badge color={REVIZYON_RENK[r.status] ?? "secondary"} className="me-2">{t(`organizator.projeDetay.revizyonDurum.${r.status}`, { defaultValue: r.status })}</Badge>
                    {r.text}
                  </div>
                ))}
              </CardBody>
            </Card>
          </>
        )}
      </Container>
    </div>
  );
}
